/**
 * useDadosDeCobranca — lógica da modal "Dados para o pagamento" (pacote e domínio).
 * Plano: docs/PLANO-DOCUMENTO-CPF-CNPJ-E-COBRANCA.md (TODO 3.3, algoritmo em §2.3).
 *
 * Uso:
 *   const cobranca = useDadosDeCobranca();
 *   cobranca.abrir(async ({ document, billingName }) => {
 *     try { ...compra...; return { ok: true }; }
 *     catch (e) { return { ok: false, erro: 'mensagem' }; }   // ou { ok: false, fechar: true }
 *   });
 *   <DadosDeCobrancaDialog vm={cobranca.vm} actions={cobranca.actions} />
 *
 * Ciclo:
 *  - abrir: busca GET /users/documents e preenche com a SUGESTÃO que a API já resolveu
 *    (cobrança salva → senão conta). Falhou o GET → abre em branco; nunca trava a compra.
 *  - confirmar: valida no front (dígito verificador, nome) e só então chama a compra com o
 *    documento LIMPO. Quem grava a cobrança é a API, DEPOIS que o pedido existe.
 */
import { useState, useCallback, useRef } from 'react';
import usersService from '../services/usersService';
import { mascararDocumento, validarDocumento } from '../utils/documento';
import { DADOS_DE_COBRANCA_FECHADO as FECHADO } from '../components/billing/dadosDeCobranca.vm';

export default function useDadosDeCobranca() {
  const [estado, setEstado] = useState(FECHADO);
  const aoConfirmar = useRef(null);
  // Cada abertura tem um número: a resposta de uma busca antiga (abriu, fechou, abriu de novo)
  // não pode preencher a modal da abertura nova.
  const abertura = useRef(0);

  const abrir = useCallback(async (onConfirm) => {
    const minha = ++abertura.current;
    aoConfirmar.current = onConfirm;
    setEstado({ ...FECHADO, open: true, loading: true });
    let name = '';
    let document = '';
    try {
      const dados = await usersService.getDocuments();
      name = dados?.billingSuggestion?.billingName ?? '';
      document = mascararDocumento(dados?.billingSuggestion?.billingDocument ?? '');
    } catch {
      // Sem os dados salvos a pessoa digita — pior seria não deixar comprar.
    }
    if (minha !== abertura.current) return;
    setEstado((e) => (e.open ? { ...e, loading: false, name, document } : e));
  }, []);

  const onChangeName = useCallback((valor) => {
    setEstado((e) => ({ ...e, name: valor, nameError: null, formError: null }));
  }, []);

  const onChangeDocument = useCallback((valor) => {
    setEstado((e) => ({ ...e, document: mascararDocumento(valor), documentError: null, formError: null }));
  }, []);

  const onCancel = useCallback(() => {
    setEstado((e) => (e.busy ? e : FECHADO));
  }, []);

  const onConfirm = useCallback(async () => {
    const atual = estado;
    if (atual.busy || atual.loading) return;

    const documento = validarDocumento(atual.document);
    const nome = (atual.name || '').trim();
    const nameError = nome ? null : 'Informe o nome ou a razão social.';
    if (!documento.valido || nameError) {
      setEstado((e) => ({ ...e, documentError: documento.erro, nameError }));
      return;
    }

    setEstado((e) => ({ ...e, busy: true, formError: null }));
    let resultado;
    try {
      resultado = await aoConfirmar.current?.({ document: documento.limpo, billingName: nome });
    } catch {
      resultado = { ok: false, erro: 'Não foi possível iniciar o pagamento. Tente novamente.' };
    }

    if (resultado?.ok || resultado?.fechar) {
      setEstado(FECHADO);
      return;
    }
    setEstado((e) => ({
      ...e,
      busy: false,
      formError: resultado?.erro || 'Não foi possível iniciar o pagamento. Tente novamente.',
    }));
  }, [estado]);

  return {
    abrir,
    vm: estado,
    actions: { onChangeName, onChangeDocument, onConfirm, onCancel },
  };
}
